import { useTranslation } from "react-i18next"
import { ArrowSquareOut, FileText, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { formatDateString } from "@/lib/format-date"
import { cn } from "@/lib/utils"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { ExtractNode } from "@/modules/patients/types/ExtractNode"

export type CarePlanTreatmentPlan = ExtractNode<
  ProgressNoteAndParentData["episodeOfCareProgressNoteData"]["carePlanSummary"]
>["treatmentPlan"]

interface TreatmentPlanDocumentProps extends DocumentProps {
  id?: string
  isEditable?: boolean
  treatmentPlan: CarePlanTreatmentPlan
}

const TreatmentPlanDocument = ({
  id,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
  treatmentPlan,
}: TreatmentPlanDocumentProps) => {
  const [t] = useTranslation()
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const isSelected = isConclusion && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
      )}
      data-testid={isConclusion ? "treatment-plan-preview-item" : "treatment-plan-document-item"}
      onClick={() => {
        if (!isEditable) {
          onSelect()
        }
      }}>
      <FileText className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {`${t("patient.conclude.treatmentPlan.treatmentPlanFor")} ${treatmentPlan?.title}`}
        </ListText>
        <ListText variant="secondary" className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {`${formatDateString(treatmentPlan?.from, "dd.MM.yyy")} - ${formatDateString(
            treatmentPlan?.to,
            "dd.MM.yyy",
          )}`}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="treatment-plan-view-document-button" />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.approved") : t("patient.conclude.approveThis")}
        />
      )}
    </ListItem>
  )
}

export { TreatmentPlanDocument }
